import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import PortalSpaceChrome from './PortalSpaceChrome';
import SpaceSwitcher from './SpaceSwitcher';
import SpaceSlot from './SpaceSlot';
import { SpaceChip, AvatarStack } from './spaceVisuals';
import { SPACES, getReachableSpaces } from './portalSpaces';
import SpaceTaskBody from './SpaceTaskBody';
import { subscribeToSpaceItems } from '../../services/SpaceItemsService';
import {
	buildCaptureSlots,
	summarizeCaptures,
} from '../../services/spaceItemsModel';
import personalTaskService from '../../services/personalTaskService';
import personalProjectService from '../../services/personalProjectService';
import ownersTaskService from '../../services/ownersTaskService';
import barbaraTaskService from '../../services/barbaraTaskService';
import SubscriptionsPanel from './Personal/SubscriptionsPanel';
import BadBibleBody from './BadBible/BadBibleBody';

/* Owner-only private-space shell: chrome + space-switcher + the space identity
   band + the space's content slots. Serves /portal/personal, /portal/owners,
   /portal/barbara — all owner-only spaces, so any reachable render is
   multi-space (owners reach all four).

   The Work space and the single-space "Dashboard" framing moved to WorkShell in
   Phase E3, so this shell no longer handles either — hence no work branch, no
   single-space framing, and no hero/reserved slots (those lived on the Work
   config).

   All three private spaces now run the real task machinery, each bound to its
   OWN collection — the collection is the wall, so a mis-scoped query returns
   nothing rather than returning Barbara's caregiving admin to a teacher.

   The client guard below redirects a user off any space they can't reach. The
   real guard is the Firestore rules on the data underneath (isOwner() on every
   private collection) — this is courtesy, not security. */

/* Which task collection each space's body is bound to. A space listed here
   renders the module; one that isn't falls back to its static slots. */
const SPACE_TASK_SERVICES = {
	personal: personalTaskService,
	owners: ownersTaskService,
	barbara: barbaraTaskService,
};

/* Which PROJECTS collection each space is bound to. Keyed only where a space
   actually has one — Personal does, Owners and Barbara do not. A space absent
   here inherits the context default (Work's read-only binding) and never reads
   it, because spaceFields withholds the project field from it and the module
   only subscribes when the field is on. Two independent guards saying the same
   thing, which is what stops a household rail listing studio projects. */
/* Spaces whose whole body is one bespoke component rather than the task
   module or slots. Only Bad Bible: a read-only browser over its sheet copy. */
const SPACE_BODIES = {
	bible: BadBibleBody,
};

const SPACE_PROJECT_SERVICES = {
	personal: personalProjectService,
};

/* Per-space PANELS rendered between the task module and the slots: whole
   surfaces a space owns that are neither tasks nor captures. Only Personal has
   one — the Subscriptions cut ledger, bound to `personalSubscriptions`, which
   is its own owner-walled collection by the 2026-07-31 decision (a
   cancellation is not a task). Owners and Barbara have none. */
const SPACE_PANELS = {
	personal: [{ key: 'subscriptions', Component: SubscriptionsPanel }],
};

/* Per-space extra slots rendered BELOW the tasks. Only Personal has any — its
   Capture inbox, which is a read-later library rather than a task list. Owners
   and Barbara have none, which is why this map is not keyed for them. */
const SLOT_BUILDERS = {
	personal: buildCaptureSlots,
};

/* Per-space switcher meta. Same reasoning as SLOT_BUILDERS: what counts as
   worth reporting is a per-space judgment. */
const SUMMARIZERS = {
	personal: summarizeCaptures,
};

function SpaceShell({ spaceKey }) {
	const { currentUser, userProfile } = useAuth();
	const space = SPACES[spaceKey];

	const reachable = getReachableSpaces(userProfile, currentUser?.email);
	const reachableKeys = reachable.map((s) => s.key);
	const canReach = Boolean(space) && reachableKeys.includes(spaceKey);

	const buildSlots = SLOT_BUILDERS[spaceKey];
	// Only subscribe for a space the user can actually reach — otherwise the
	// snapshot fires a permission-denied on its way to the redirect below.
	const liveCaptures = canReach && buildSlots ? space.live?.captures || null : null;

	const [captures, setCaptures] = useState([]);
	const [loadError, setLoadError] = useState(null);

	/* Hooks run before the reachability redirect on purpose: bailing early would
	   change the hook count between renders and React would throw. The effect
	   no-ops instead, via the null liveCaptures above. */
	useEffect(() => {
		if (!liveCaptures) return undefined;

		setLoadError(null);
		return subscribeToSpaceItems(liveCaptures, setCaptures, setLoadError);
	}, [liveCaptures]);

	// Off-limits space (e.g. a non-owner typing /portal/owners) → bounce to their
	// first reachable space. Unknown key → same.
	if (!canReach) {
		const fallback = reachable[0]?.route || '/portal/work';
		return <Navigate to={fallback} replace />;
	}

	const taskService = SPACE_TASK_SERVICES[spaceKey];
	const CustomBody = SPACE_BODIES[spaceKey];
	const projectService = SPACE_PROJECT_SERVICES[spaceKey];
	const panels = SPACE_PANELS[spaceKey] || [];
	/* A space running the module owns its whole body: its static design-copy
	   slots are superseded, so they must NOT fall through as extra slots
	   underneath the task list. Only a builder-produced list rides along. */
	const slots = buildSlots
		? buildSlots(captures)
		: taskService
		? []
		: space.slots || [];

	const summarize = SUMMARIZERS[spaceKey];
	const liveSummary = summarize ? { [spaceKey]: summarize(captures) } : null;

	const accentStyle = { '--psp-accent': `var(--space-${space.key})` };

	return (
		<PortalSpaceChrome
			leading={
				<SpaceSwitcher
					spaces={reachable}
					current={space}
					liveSummary={liveSummary}
				/>
			}>
			{/* A module body needs far more width than the Phase B card stack. */}
			<div className={`psp-page${taskService || CustomBody ? ' psp-page--wide' : ''}`}>
				{/* Space-identity band. */}
				<div className='psp-shell-head' style={accentStyle}>
					<span className='psp-shell-head__bar' />
					<SpaceChip space={space} size={44} radius={13} />
					<div className='psp-shell-head__body'>
						<div className='psp-shell-head__namerow'>
							<span className='psp-shell-head__name'>
								{space.name}
							</span>
							<span className='psp-pill psp-pill--accent'>
								{space.privacy}
							</span>
						</div>
						<div className='psp-shell-head__sub'>{space.sub}</div>
					</div>
					<AvatarStack members={space.members} size={24} />
				</div>

				{/* A failed read must not read as an empty space — an empty slot
				    and a denied one look identical otherwise. */}
				{loadError && (
					<div className='psp-shell-error' role='status'>
						<i className='fa-sharp fa-solid fa-triangle-exclamation' />
						<span>
							Couldn't load this space's items. The list below may be
							incomplete.
						</span>
					</div>
				)}

				{/* A space with a real module renders it and hands its slots
				    through; a still-static space renders slots alone. */}
				{CustomBody ? (
					<div style={accentStyle}>
						<CustomBody />
					</div>
				) : taskService ? (
					<div style={accentStyle}>
						<SpaceTaskBody
						service={taskService}
						projectService={projectService}
						panels={panels}
						slots={slots}
					/>
					</div>
				) : (
					<div className='psp-slots' style={accentStyle}>
						{slots.map((slot) => (
							<SpaceSlot key={slot.key || slot.name} {...slot} />
						))}
					</div>
				)}
			</div>
		</PortalSpaceChrome>
	);
}

export default SpaceShell;
